/**
 * Accessible modal dialog component.
 *
 * Features:
 * - role="dialog" + aria-modal="true" + aria-labelledby
 * - Focus trap: Tab / Shift+Tab stay within the modal
 * - ESC key closes the modal (unless a nested widget already claimed the key, or
 *   an IME composition is in progress)
 * - Returns focus to the trigger element on close
 * - Backdrop click closes (optional, default on) — but only when the press *and*
 *   the release both land on the backdrop itself. See handleBackdropPointerDown.
 */
import { useEffect, useRef, useCallback } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

interface ModalProps {
  /** Must be provided — used as aria-labelledby target */
  titleId: string
  title: React.ReactNode
  /** Optional subtitle rendered below title in muted text */
  subtitle?: React.ReactNode
  onClose: () => void
  /** Whether backdrop click closes the modal (default true) */
  closeOnBackdrop?: boolean
  /** Show an × button in the top-right corner of the header (default false) */
  showCloseButton?: boolean
  children: React.ReactNode
  /** Max width class, e.g. 'max-w-md' (default 'max-w-lg') */
  maxWidth?: string
}

export default function Modal({
  titleId,
  title,
  subtitle,
  onClose,
  closeOnBackdrop = true,
  showCloseButton = false,
  children,
  maxWidth = 'max-w-lg',
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  // Remember element that had focus before modal opened
  const previousFocus = useRef<Element | null>(document.activeElement)
  // Keep onClose in a ref so the keydown handler always has the latest version
  // without needing to be in the effect's dependency array (which would re-run
  // the effect — and re-focus the first element — on every parent re-render).
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  // Stable callback so parent re-renders don't recreate this function
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (e.key === 'Escape') {
      // Don't close if something inside already acted on this Escape — a nested
      // popover, a combobox clearing itself, an inner dialog. Those call
      // preventDefault() to claim the key; we honour that.
      if (e.defaultPrevented) return
      // Mid-IME-composition, Escape cancels the candidate list, not the dialog.
      // keyCode 229 is the pre-`isComposing` signal some browsers still send.
      if (e.isComposing || e.keyCode === 229) return
      // Nested dialogs: this listener sits on our own panel, and an inner
      // dialog's panel is a DOM descendant of ours. Without this the inner
      // dialog's Escape would keep bubbling and close us too.
      e.stopPropagation()
      onCloseRef.current()
      return
    }
    if (e.key !== 'Tab') return

    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }, [])

  // Mount-only effect: set initial focus and wire the keydown listener.
  // This must NOT re-run on re-renders — re-running would call firstFocusable.focus()
  // on every keystroke and steal focus from whichever input the user is typing in.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const firstFocusable = dialog.querySelectorAll<HTMLElement>(FOCUSABLE)[0]
    firstFocusable?.focus()

    // Read the ref HERE, not in the cleanup. It is set once at first render and
    // never reassigned, so the value is identical either way — but reading a ref
    // during cleanup is the pattern that silently breaks when the ref does start
    // changing, which is what the lint rule is warning about.
    const restoreFocusTo = previousFocus.current as HTMLElement | null

    dialog.addEventListener('keydown', handleKeyDown)
    return () => {
      dialog.removeEventListener('keydown', handleKeyDown)
      restoreFocusTo?.focus()
    }
  }, [handleKeyDown])

  // --- Backdrop dismissal ------------------------------------------------
  //
  // A DOM `click` is dispatched on the nearest common ancestor of the press
  // target and the release target. So a drag that STARTS inside the panel
  // (selecting the text in an input, or grabbing the panel's scrollbar) and
  // ENDS a few pixels outside it dispatches its `click` on the *backdrop* —
  // which used to close the dialog and throw away whatever was typed.
  //
  // The fix is to close only when the whole interaction — press and release —
  // happened on the backdrop itself. Both halves are needed: the press check
  // rejects the drag-out-of-the-panel case, and the release check rejects the
  // reverse (press on the backdrop, release inside the panel), which also
  // reports the backdrop as the click target. It equally covers the case where
  // the pressed element unmounts mid-interaction: a detached press target is
  // not the backdrop, so nothing closes.
  //
  // We deliberately do NOT close on the press alone. That would dismiss the
  // dialog before a click could land on anything, and it breaks the
  // click-outside convention users expect (press outside, notice the mistake,
  // drag back in, release — no dismissal).
  const pressedBackdrop = useRef(false)
  const releasedBackdrop = useRef(false)

  const handleBackdropPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // e.button === 0 keeps middle/right presses from arming a dismissal.
    pressedBackdrop.current = e.target === e.currentTarget && e.button === 0
    releasedBackdrop.current = false
  }

  const handleBackdropPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    releasedBackdrop.current = e.target === e.currentTarget
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const pressed = pressedBackdrop.current
    const released = releasedBackdrop.current
    pressedBackdrop.current = false
    releasedBackdrop.current = false
    if (!pressed || !released) return
    if (e.target !== e.currentTarget) return
    onClose()
  }

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      aria-hidden="false"
      onPointerDown={closeOnBackdrop ? handleBackdropPointerDown : undefined}
      onPointerUp={closeOnBackdrop ? handleBackdropPointerUp : undefined}
      onClick={closeOnBackdrop ? handleBackdropClick : undefined}
    >
      {/*
        Dialog panel. No onClick={stopPropagation} here on purpose: the backdrop
        handler above already ignores anything whose press, release or click
        target isn't the backdrop element itself, and swallowing every click
        inside the panel would silently break any document-level listener.
      */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`bg-white rounded-lg shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}
      >
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 id={titleId} className="text-lg font-semibold text-gray-900">
                {title}
              </h3>
              {subtitle && (
                <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
              )}
            </div>
            {showCloseButton && (
              <button
                aria-label="Close"
                onClick={onClose}
                className="tap-icon flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
          <div className={subtitle || title ? 'mt-4' : ''}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Convenience sub-components
// ---------------------------------------------------------------------------

/** Standard footer row for Cancel + primary action */
export function ModalFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
      {children}
    </div>
  )
}
